
import {
  Activity,
  Award,
  CheckCircle2,
  ChevronRight,
  FileText,
  Maximize,
  MessageSquare,
  Pause,
  Play,
  UserCheck,
  Video,
  Volume2,
  VolumeX,
} from "lucide-react";

import { mapCandidateDetails } from "@/modules/hrPortal/utils/candidateDetailsMapper";
import { formatMonthYear } from "@/modules/hrPortal/utils/formatters";
import useCandidateVideo from "@/modules/hrPortal/hooks/useCandidateVideo";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Progress } from "@/shared/components/ui/progress";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";

type CandidateDetailsViewModel = ReturnType<typeof mapCandidateDetails>;
type AIAnalysis = CandidateDetailsViewModel["aiAnalysis"];
type Video = CandidateDetailsViewModel["video"];

interface CandidateAnalysisTabsProps {
  aiAnalysis: AIAnalysis;
  video: Video;
  applicationId: string;
  statusUpdating: boolean;
  onMessage: (message: string) => void;
}

export default function CandidateAnalysisTabs({
  aiAnalysis,
  video,
  applicationId,
  statusUpdating,
  onMessage,
}: CandidateAnalysisTabsProps) {
  const {
    videoRef,
    videoUrl,
    isVideoPlaying,
    isMuted,
    playVideo,
    toggleMute,
    toggleFullscreen,
    setIsVideoPlaying,
  } = useCandidateVideo({
    applicationId,
    hasVideo: Boolean(video),
    onError: onMessage,
  });

  return (
    <Tabs defaultValue="resume" className="w-full min-w-0 space-y-4">
      {/* Responsive tabs navigation */}
      <div className="w-full min-w-0 overflow-x-auto">
        <TabsList className="flex h-auto w-max min-w-full flex-nowrap justify-start gap-1 bg-white p-1 sm:w-auto sm:min-w-0">
          <TabsTrigger
            value="resume"
            className="shrink-0 gap-1.5 whitespace-nowrap px-3 py-2 text-xs sm:gap-2 sm:text-sm"
          >
            <FileText className="h-4 w-4 shrink-0" />
            Resume Analysis
          </TabsTrigger>

          <TabsTrigger
            value="video"
            className="shrink-0 gap-1.5 whitespace-nowrap px-3 py-2 text-xs sm:gap-2 sm:text-sm"
          >
            <Video className="h-4 w-4 shrink-0" />
            Video Analysis
          </TabsTrigger>

          <TabsTrigger
            value="interview"
            className="shrink-0 gap-1.5 whitespace-nowrap px-3 py-2 text-xs sm:gap-2 sm:text-sm"
          >
            <MessageSquare className="h-4 w-4 shrink-0" />
            Interview Prep
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Resume Analysis */}
      <TabsContent value="resume" className="mt-0 min-w-0">
        <Card className="w-full min-w-0">
          <CardContent className="min-w-0 p-4 sm:p-5 lg:p-6">
            <div className="grid min-w-0 gap-6">
              {/* Extracted Skills */}
              <div className="min-w-0">
                <h4 className="mb-3 font-semibold">Extracted Skills</h4>

                <div className="flex flex-wrap gap-2">
                  {aiAnalysis.resumeAnalysis.skills.map((skill) => (
                    <Badge
                      key={skill}
                      variant="outline"
                      className="max-w-full break-words"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Work Experience */}
              <div className="min-w-0">
                <h4 className="mb-3 font-semibold">Work Experience</h4>

                <div className="space-y-4">
                  {aiAnalysis.resumeAnalysis.experience.map((exp, index) => (
                    <div
                      key={index}
                      className="min-w-0 border-l-2 border-blue-200 pl-3 sm:pl-4"
                    >
                      <div className="mb-3 flex min-w-0 flex-col items-start justify-between gap-2 sm:flex-row sm:gap-3">
                        <div className="min-w-0">
                          <h5 className="break-words font-medium">
                            {exp.role}
                          </h5>

                          <p className="mt-1 break-words text-sm text-muted-foreground">
                            {exp.company}
                          </p>
                        </div>

                        <Badge
                          variant="outline"
                          className={`max-w-full whitespace-normal break-words ${
                            exp.currentlyWorking
                              ? "border-green-200 bg-green-50 text-green-700"
                              : ""
                          }`}
                        >
                          {exp.currentlyWorking
                            ? "Currently Working"
                            : exp.duration}
                        </Badge>
                      </div>

                      <div className="mb-3 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3">
                        <div className="min-w-0 rounded-md bg-slate-50 p-3">
                          <p className="text-xs text-muted-foreground">
                            Start Date
                          </p>
                          <p className="mt-1 break-words text-sm font-medium">
                            {formatMonthYear(exp.startDate)}
                          </p>
                        </div>

                        <div className="min-w-0 rounded-md bg-slate-50 p-3">
                          <p className="text-xs text-muted-foreground">
                            End Date
                          </p>
                          <p className="mt-1 break-words text-sm font-medium">
                            {exp.currentlyWorking
                              ? "Present"
                              : formatMonthYear(exp.endDate)}
                          </p>
                        </div>

                        <div className="min-w-0 rounded-md bg-slate-50 p-3">
                          <p className="text-xs text-muted-foreground">
                            Duration
                          </p>
                          <p className="mt-1 break-words text-sm font-medium">
                            {exp.duration}
                          </p>
                        </div>
                      </div>

                      <ul className="list-inside list-disc space-y-1 break-words text-sm text-muted-foreground">
                        {exp.highlights.map((highlight, highlightIndex) => (
                          <li key={highlightIndex}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div className="min-w-0">
                <h4 className="mb-3 font-semibold">Education</h4>

                <div className="space-y-3">
                  {aiAnalysis.resumeAnalysis.education.map((edu, index) => (
                    <div
                      key={index}
                      className="min-w-0 rounded-lg border bg-slate-50 p-3 sm:p-4"
                    >
                      <div className="flex min-w-0 flex-col items-start justify-between gap-2 sm:flex-row sm:gap-3">
                        <div className="min-w-0">
                          <p className="break-words font-medium">
                            {edu.degree}
                          </p>

                          <p className="mt-1 break-words text-sm text-muted-foreground">
                            {edu.institution}
                          </p>
                        </div>

                        {edu.year && edu.year !== "N/A" && (
                          <span className="shrink-0 text-sm font-medium text-slate-600">
                            {edu.year}
                          </span>
                        )}
                      </div>

                      {edu.score && edu.score !== "N/A" && (
                        <p className="mt-2 break-words text-sm text-muted-foreground">
                          Score: {edu.score}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div className="min-w-0">
                <h4 className="mb-3 font-semibold">Certifications</h4>

                {aiAnalysis.resumeAnalysis.certifications.length > 0 ? (
                  <div className="space-y-3">
                    {aiAnalysis.resumeAnalysis.certifications.map(
                      (certification, index) => (
                        <div
                          key={`${certification.name}-${index}`}
                          className="min-w-0 rounded-lg border bg-slate-50 p-3 sm:p-4"
                        >
                          <div className="flex min-w-0 flex-col items-start justify-between gap-2 sm:flex-row sm:gap-3">
                            <div className="min-w-0">
                              <h5 className="break-words font-medium">
                                {certification.name || "Certification"}
                              </h5>

                              {certification.issuer && (
                                <p className="mt-1 break-words text-sm text-muted-foreground">
                                  {certification.issuer}
                                </p>
                              )}
                            </div>

                            {certification.neverExpires && (
                              <Badge
                                variant="secondary"
                                className="shrink-0"
                              >
                                No Expiry
                              </Badge>
                            )}
                          </div>

                          {certification.credentialId && (
                            <p className="mt-3 break-all text-sm text-muted-foreground">
                              <span className="font-medium text-slate-700">
                                Credential ID:
                              </span>{" "}
                              {certification.credentialId}
                            </p>
                          )}

                          {certification.issuedDate && (
                            <p className="mt-1 break-words text-sm text-muted-foreground">
                              <span className="font-medium text-slate-700">
                                Issued:
                              </span>{" "}
                              {certification.issuedDate}
                            </p>
                          )}

                          {!certification.neverExpires &&
                            certification.expiryDate && (
                              <p className="mt-1 break-words text-sm text-muted-foreground">
                                <span className="font-medium text-slate-700">
                                  Expires:
                                </span>{" "}
                                {certification.expiryDate}
                              </p>
                            )}

                          {certification.credentialUrl && (
                            <a
                              href={certification.credentialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 inline-block break-all text-sm font-medium text-blue-600 hover:underline"
                            >
                              View Credential
                            </a>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No certifications found in the resume.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Video Analysis */}
      <TabsContent value="video" className="mt-0 min-w-0">
        <Card className="w-full min-w-0">
          <CardContent className="min-w-0 p-4 sm:p-5 lg:p-6">
            {/* Video player */}
            <div className="relative mb-5 flex aspect-video w-full min-w-0 items-center justify-center overflow-hidden rounded-lg bg-slate-900 sm:mb-6">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />

              {videoUrl ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  muted={isMuted}
                  controls
                  autoPlay
                  playsInline
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  className="relative z-10 h-full w-full object-contain"
                />
              ) : (
                <button
                  type="button"
                  onClick={playVideo}
                  disabled={!video || statusUpdating}
                  aria-label={isVideoPlaying ? "Pause video" : "Play video"}
                  className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm disabled:cursor-not-allowed disabled:opacity-50 sm:h-20 sm:w-20"
                >
                  {isVideoPlaying ? (
                    <Pause className="h-8 w-8 sm:h-10 sm:w-10" />
                  ) : (
                    <Play className="ml-1 h-8 w-8 sm:h-10 sm:w-10" />
                  )}
                </button>
              )}

              {/* Video controls overlay */}
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent p-2 sm:p-4">
                <div className="pointer-events-auto flex min-w-0 items-center gap-2 sm:gap-4">
                  <Progress value={45} className="h-1 min-w-0 flex-1" />

                  <span className="shrink-0 text-xs text-white sm:text-sm">
                    0:54 / 2:00
                  </span>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={isMuted ? "Unmute video" : "Mute video"}
                    onClick={toggleMute}
                    className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white sm:h-9 sm:w-9"
                  >
                    {isMuted ? (
                      <VolumeX className="h-4 w-4" />
                    ) : (
                      <Volume2 className="h-4 w-4" />
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Toggle fullscreen"
                    onClick={toggleFullscreen}
                    className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white sm:h-9 sm:w-9"
                  >
                    <Maximize className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Primary video metrics */}
            <div className="mb-5 grid min-w-0 grid-cols-1 gap-3 sm:mb-6 sm:grid-cols-3 sm:gap-4">
              <div className="min-w-0 rounded-lg bg-green-50 p-3 text-center">
                <Activity className="mx-auto mb-1 h-5 w-5 text-green-600" />

                <p className="break-words text-xl font-bold text-green-600 sm:text-2xl">
                  {aiAnalysis.videoAnalysis.confidence != null
                    ? `${aiAnalysis.videoAnalysis.confidence}%`
                    : "N/A"}
                </p>

                <p className="text-xs text-muted-foreground">
                  Confidence
                </p>
              </div>

              <div className="min-w-0 rounded-lg bg-blue-50 p-3 text-center">
                <MessageSquare className="mx-auto mb-1 h-5 w-5 text-blue-600" />

                <p className="break-words text-xl font-bold text-blue-600 sm:text-2xl">
                  {aiAnalysis.videoAnalysis.communication}%
                </p>

                <p className="text-xs text-muted-foreground">
                  Communication
                </p>
              </div>

              <div className="min-w-0 rounded-lg bg-purple-50 p-3 text-center">
                <Award className="mx-auto mb-1 h-5 w-5 text-purple-600" />

                <p className="break-words text-xl font-bold text-purple-600 sm:text-2xl">
                  {aiAnalysis.videoAnalysis.professionalism}%
                </p>

                <p className="text-xs text-muted-foreground">
                  Professionalism
                </p>
              </div>
            </div>

            {/* Detailed video metrics */}
            <div className="mb-5 grid min-w-0 grid-cols-2 gap-3 sm:mb-6 sm:grid-cols-3 lg:grid-cols-4">
              {[
                {
                  label: "Fluency",
                  value: aiAnalysis.videoAnalysis.fluency,
                },
                {
                  label: "Energy",
                  value: aiAnalysis.videoAnalysis.energy,
                },
                {
                  label: "Eye Contact",
                  value: aiAnalysis.videoAnalysis.eyeContact,
                },
                {
                  label: "Voice Clarity",
                  value: aiAnalysis.videoAnalysis.voiceClarity,
                },
                {
                  label: "Grammar",
                  value: aiAnalysis.videoAnalysis.grammar,
                },
                {
                  label: "Body Language",
                  value: aiAnalysis.videoAnalysis.bodyLanguage,
                },
              ].map((metric) => (
                <div
                  key={metric.label}
                  className="min-w-0 rounded bg-slate-50 p-3 text-center"
                >
                  <p className="break-words text-lg font-bold">
                    {metric.value != null ? `${metric.value}%` : "N/A"}
                  </p>

                  <p className="break-words text-xs text-muted-foreground">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Key points */}
            <div className="min-w-0">
              <h4 className="mb-3 font-semibold">
                Key Points from Video
              </h4>

              <ul className="space-y-2">
                {aiAnalysis.videoAnalysis.keyPoints.map((point, index) => (
                  <li
                    key={index}
                    className="flex min-w-0 items-start gap-2 text-sm"
                  >
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />

                    <span className="min-w-0 break-words text-muted-foreground">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Interview Preparation */}
      <TabsContent value="interview" className="mt-0 min-w-0">
        <Card className="w-full min-w-0">
          <CardContent className="min-w-0 p-4 sm:p-5 lg:p-6">
            {/* Recommendation */}
            <div className="mb-5 flex min-w-0 items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3 sm:mb-6 sm:p-4">
              <UserCheck className="mt-0.5 h-6 w-6 shrink-0 text-green-600" />

              <div className="min-w-0">
                <h4 className="break-words font-semibold text-green-800">
                  Recommended to Proceed
                </h4>

                <p className="mt-1 break-words text-sm text-green-700">
                  Next step: {aiAnalysis.interviewRecommendation.round}
                </p>
              </div>
            </div>

            {/* Focus areas */}
            <div className="mb-5 min-w-0 sm:mb-6">
              <h4 className="mb-3 font-semibold">
                Focus Areas for Interview
              </h4>

              <div className="flex flex-wrap gap-2">
                {aiAnalysis.interviewRecommendation.focusAreas.map((area) => (
                  <Badge
                    key={area}
                    variant="secondary"
                    className="max-w-full whitespace-normal break-words bg-yellow-100 text-yellow-700"
                  >
                    {area}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Suggested questions */}
            <div className="min-w-0">
              <h4 className="mb-3 font-semibold">
                Suggested Interview Questions
              </h4>

              <div className="space-y-3">
                {aiAnalysis.interviewRecommendation.suggestedQuestions.map(
                  (question, index) => (
                    <div
                      key={index}
                      className="flex min-w-0 items-start gap-3 rounded-lg bg-slate-50 p-3"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600">
                        {index + 1}
                      </span>

                      <p className="min-w-0 break-words text-sm text-muted-foreground">
                        {question}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
